import React from 'react';
import PropTypes from 'prop-types';
import { ArrowRight, TrendingUp } from 'lucide-react';
import { Button } from '../../../../../components/ui/button';
import { TableCell, TableRow } from '../../../../../components/ui/table';
import { BigHead } from '../../../../../lib/core.cjs.development';

function getTimeAgo(date) {
  const now = new Date();
  const diffInMs = now - date;
  const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));

  if (diffInDays === 0) {
    const diffInHours = Math.floor(diffInMs / (1000 * 60 * 60));
    if (diffInHours === 0) {
      const diffInMinutes = Math.floor(diffInMs / (1000 * 60));
      return diffInMinutes <= 1 ? 'Just now' : `${diffInMinutes} minutes ago`;
    }
    return diffInHours === 1 ? '1 hour ago' : `${diffInHours} hours ago`;
  }
  if (diffInDays === 1) return 'Yesterday';
  if (diffInDays < 7) return `${diffInDays} days ago`;
  if (diffInDays < 30) {
    const weeks = Math.floor(diffInDays / 7);
    return weeks === 1 ? '1 week ago' : `${weeks} weeks ago`;
  }
  const months = Math.floor(diffInDays / 30);
  return months === 1 ? '1 month ago' : `${months} months ago`;
}

function ReviewsTableRow({ review, onReview }) {
  const { goal, user, createdAt } = review;

  const formattedDate = new Date(createdAt).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });

  const timeAgo = getTimeAgo(new Date(createdAt));

  return (
    <TableRow className="hover:bg-muted/50">
      {/* Avatar */}
      <TableCell className="p-2">
        <div className="h-10 w-10 rounded-full overflow-hidden bg-muted">
          <BigHead {...user?.avatar} />
        </div>
      </TableCell>

      {/* User Info */}
      <TableCell>
        <div>
          <p className="font-medium text-sm">{user?.email}</p>
          {(user?.firstName || user?.lastName) && (
            <p className="text-xs text-muted-foreground">
              {user?.firstName} {user?.lastName}
            </p>
          )}
        </div>
      </TableCell>

      {/* Goal */}
      <TableCell className="text-center">
        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-amber-500/10 text-amber-600">
          <TrendingUp className="h-3 w-3" />
          {goal}
        </span>
      </TableCell>

      {/* Requested Date */}
      <TableCell className="text-center">
        <div>
          <p className="text-sm">{formattedDate}</p>
          <p className="text-xs text-muted-foreground">{timeAgo}</p>
        </div>
      </TableCell>

      {/* Action */}
      <TableCell>
        <Button size="sm" onClick={() => onReview(review)}>
          Review
          <ArrowRight className="h-4 w-4 ml-1" />
        </Button>
      </TableCell>
    </TableRow>
  );
}

ReviewsTableRow.propTypes = {
  review: PropTypes.shape({
    _id: PropTypes.string.isRequired,
    goal: PropTypes.string.isRequired,
    user: PropTypes.shape({
      email: PropTypes.string.isRequired,
      firstName: PropTypes.string,
      lastName: PropTypes.string,
      avatar: PropTypes.shape({}).isRequired,
    }).isRequired,
    createdAt: PropTypes.string.isRequired,
  }).isRequired,
  onReview: PropTypes.func.isRequired,
};

export default ReviewsTableRow;
